import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface AdminUser {
  id: string;
  name: string;
  phone: string;
  email?: string;
  role_id?: string;
  role_name?: string;
  is_active: boolean;
  permissions: string[];
}

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);

  // Check if already logged in
  useEffect(() => {
    const checkSession = () => {
      const savedUser = localStorage.getItem('admin_user');
      if (savedUser) {
        try {
          const user = JSON.parse(savedUser) as AdminUser;
          if (user.is_active) {
            navigate('/admin', { replace: true });
            return;
          }
        } catch {
          localStorage.removeItem('admin_user');
        }
      }
      setCheckingSession(false);
    };
    checkSession();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Phone number zaroori hai!');
      return;
    }
    if (!password.trim()) {
      setError('Password zaroori hai!');
      return;
    }

    setLoading(true);

    try {
      // Normalize phone number
      const normalizedPhone = phone.replace(/\D/g, '').replace(/^(91|0)/, '');

      // Fetch user from admin_users table
      const { data: userData, error: userError } = await supabase
        .from('admin_users')
        .select('id, name, phone, email, role_id, role_name, is_active, password')
        .eq('phone', normalizedPhone)
        .maybeSingle();

      if (userError || !userData) {
        setError('Galat phone number ya password!');
        setLoading(false);
        return;
      }

      // Check if user is active
      if (!userData.is_active) {
        setError('Yeh account inactive hai. Admin se contact karo.');
        setLoading(false);
        return;
      }

      // Check password (plain text for now - can be hashed later)
      if (userData.password !== password.trim()) {
        setError('Galat phone number ya password!');
        setLoading(false);
        return;
      }

      // Fetch role permissions
      let permissions: string[] = [];
      if (userData.role_id) {
        const { data: roleData } = await supabase
          .from('admin_roles')
          .select('permissions')
          .eq('id', userData.role_id)
          .maybeSingle();
        
        if (roleData?.permissions) {
          permissions = Array.isArray(roleData.permissions) ? roleData.permissions : [];
        }
      }

      // Create user object
      const adminUser: AdminUser = {
        id: userData.id,
        name: userData.name,
        phone: userData.phone,
        email: userData.email,
        role_id: userData.role_id,
        role_name: userData.role_name,
        is_active: userData.is_active,
        permissions,
      };

      // Save to localStorage
      localStorage.setItem('admin_user', JSON.stringify(adminUser));
      localStorage.setItem('admin_login_time', new Date().toISOString());

      // Update last login
      await supabase
        .from('admin_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', userData.id);

      // Redirect to admin panel
      navigate('/admin', { replace: true });

    } catch (err) {
      console.error('Login error:', err);
      setError('Kuch galat ho gaya. Dobara try karo.');
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-slate-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 px-4 py-4">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
            <i className="ri-shield-star-line text-white text-lg"></i>
          </div>
          <div>
            <h1 className="text-white font-bold text-base">ClothyBazar Admin</h1>
            <p className="text-slate-400 text-xs">Staff Login Portal</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-user-line text-3xl text-white"></i>
              </div>
              <h2 className="text-white font-bold text-xl">Staff Login</h2>
              <p className="text-slate-400 text-sm mt-1">Phone + Password se login karo</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="p-6 space-y-5">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 shrink-0 mt-0.5"></i>
                  <p className="text-red-600 text-xs">{error}</p>
                </div>
              )}

              {/* Phone Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
                  <i className="ri-phone-line text-gray-500"></i>
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="98765 43210"
                    maxLength={10}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 border-gray-200 text-sm focus:border-slate-900 focus:ring-0 outline-none transition-all"
                    disabled={loading}
                  />
                </div>
                <p className="text-gray-400 text-[11px] mt-1.5 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  Yeh phone number aapka login ID hai
                </p>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
                  <i className="ri-lock-password-line text-gray-500"></i>
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Apna password daalo..."
                    className="w-full pl-4 pr-12 py-3.5 rounded-xl border-2 border-gray-200 text-sm focus:border-slate-900 focus:ring-0 outline-none transition-all"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    tabIndex={-1}
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading || phone.length < 10 || !password}
                className="w-full py-4 rounded-xl bg-slate-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Login ho raha hai...
                  </>
                ) : (
                  <>
                    <i className="ri-login-box-line text-lg"></i>
                    Login Karo
                  </>
                )}
              </button>

              {/* Help Text */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-slate-700 text-xs font-semibold mb-2 flex items-center gap-1">
                  <i className="ri-question-line text-slate-500"></i>
                  Login nahi ho raha?
                </p>
                <ul className="text-slate-500 text-xs space-y-1">
                  <li className="flex items-start gap-1.5">
                    <i className="ri-check-line text-slate-400 shrink-0"></i>
                    <span>Phone number admin ne add kiya hai ya nahi check karo</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-check-line text-slate-400 shrink-0"></i>
                    <span>Password case-sensitive hai (capital/small letters matter karte hain)</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-check-line text-slate-400 shrink-0"></i>
                    <span>Account active hai ya nahi admin se confirm karo</span>
                  </li>
                </ul>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-gray-400 text-xs mt-6">
            <i className="ri-shield-check-line mr-1"></i>
            Secure Admin Portal · ClothyBazar
          </p>
        </div>
      </main>
    </div>
  );
}
